import { useSuspenseQuery } from '@apollo/client/react';
import { Fragment, useRef, useState } from 'react';
import { Download, ArrowLeft, ArrowRight } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BackupAssetReportDocument } from 'src/graphql/actions/reports/backupAssetReport.query';
import { withSuspense } from 'src/hoc';

import { ReportLayout, ContentVerification, ActivityVerification } from '.';

import { backupLayoutContentMap, type IAssetReport } from '../model';

const setOptions = (
  type: 'content' | 'activity',
  onBeforePrint?: () => Promise<void>,
  onPrint?: () => Promise<void>,
) => ({
  onBeforePrint,
  print: async (printIframe: HTMLIFrameElement) => {
    const document = printIframe.contentDocument;
    const reportLayout = document?.body.children[0];

    if (!reportLayout) return;

    try {
      await html2pdf()
        .set({
          image: {
            type: 'jpeg',
            quality: 0.98,
          },
          jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
          html2canvas: { scale: 2 },
          pagebreak: { mode: 'avoid-all' },
          filename: `${type}-verification-report.pdf`,
        })
        .from(reportLayout)
        .save();
    } finally {
      if (onPrint) onPrint();
    }
  },
  copyStyles: true,
  pageStyle: 'img { display: unset; }',
});

const reportContentMap = {
  content: ContentVerification,
  activity: ActivityVerification,
};

type Props = {
  reportId: string;
  backupId: string;
  canDownload: boolean;
  triggerNode?: React.ReactNode;
};

const ReportContent = withSuspense(({ reportId, backupId, canDownload }: Props) => {
  const [activeReport, setActiveReport] = useState<'content' | 'activity'>('content');
  const [isLoading, setIsLoading] = useState(false);
  const {
    data: { backupAssetReport },
  } = useSuspenseQuery(BackupAssetReportDocument, {
    variables: {
      backupId,
      backupAssetReportId: reportId,
    },
  });
  const assetReport: IAssetReport = backupAssetReport
    ? {
        period: backupAssetReport.period,
        asset: { depositSource: backupAssetReport.backupAsset.name },
        data: backupAssetReport.data,
      }
    : null;
  const contentTargetRef = useRef<HTMLDivElement>(null);
  const onDownloadReport = useReactToPrint({
    contentRef: contentTargetRef,
    ...setOptions(
      activeReport,
      async () => setIsLoading(true),
      async () => setIsLoading(false),
    ),
  });

  const ActiveReportContent = reportContentMap[activeReport];

  const onPrevious = () => setActiveReport('content');

  const onNext = () => setActiveReport('activity');

  return (
    <Fragment>
      <div className="grow overflow-y-auto bg-muted p-6">
        <ReportLayout
          content={backupLayoutContentMap[activeReport]}
          assetName={backupAssetReport?.backupAsset.name ?? ''}
          period={backupAssetReport?.period}
          generatedAt={backupAssetReport?.createdAt}
          ref={contentTargetRef}
        >
          <ActiveReportContent assetReport={assetReport} />
        </ReportLayout>
      </div>
      <DialogFooter className="flex-row justify-between sm:justify-between items-center border-t px-6 pt-4">
        <div className="flex items-center gap-4 sm:gap-6">
          <Button
            variant="secondary"
            size="icon"
            className="shrink-0 size-8 rounded-full"
            onClick={onPrevious}
            disabled={activeReport === 'content'}
          >
            <ArrowLeft />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="shrink-0 size-8 rounded-full"
            onClick={onNext}
            disabled={activeReport === 'activity'}
          >
            <ArrowRight />
          </Button>
        </div>
        {canDownload && (
          <Button onClick={() => onDownloadReport()} disabled={isLoading} isLoading={isLoading}>
            <Download />
            Download
          </Button>
        )}
      </DialogFooter>
    </Fragment>
  );
});

const BackupAssetReportContainer = ({ reportId, backupId, canDownload, triggerNode }: Props) => {
  return (
    <Dialog>
      <DialogTrigger asChild>{triggerNode}</DialogTrigger>
      <DialogContent className="flex flex-col sm:max-w-161.25 min-h-125 max-h-205 px-0 gap-0 lg:my-10">
        <DialogHeader className="px-6 pb-4 border-b">
          <DialogTitle className="text-base">Report</DialogTitle>
          <DialogDescription className="sr-only">Report</DialogDescription>
        </DialogHeader>
        <ReportContent reportId={reportId} backupId={backupId} canDownload={canDownload} />
      </DialogContent>
    </Dialog>
  );
};

export default BackupAssetReportContainer;
